import { Component, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';
import { UntilDestroy } from '@ngneat/until-destroy';
import { FlexModule } from '@angular/flex-layout/flex';
import {
  AzureStorageDocument,
  RequiredTrustDeed,
  UploadAzureFile,
  UploadAzureFiles,
} from '@portal-workspace/grow-shared-library';
import { setupUntilDestroy } from '../component-utils';
import { UploadFileComponent } from '../upload-file-component/upload-file.component';
import { UploadedFilesListComponent } from '../upload-file-component/uploaded-files-list.component';
import { MessageBoxComponent } from '../message-box/message-box.component';

/**
 * The Trust Deed Upload step in the submission wizard.
 *
 * One upload box per deed the application requires, labelled and explained with the same wording
 * the Documents tab uses for the matching standard condition, so the broker reads the same thing in
 * both places. Uploading is optional throughout — brokers frequently do not have deeds to hand, and
 * every box becomes a condition on the Documents tab whether or not anything is put in it.
 *
 * No analysis runs here. Deeds are stored against their condition and analysed when the application
 * is submitted, so a draft that is abandoned never costs a FrankieOne workflow. That also means the
 * step shows file names only, with no analysis status, result or error.
 */

/** Deeds selected in the step, each tagged with the condition it belongs to. */
export type TrustDeedUploadValue = UploadAzureFiles | null;

@UntilDestroy({ arrayName: 'subscriptions' })
@Component({
  selector: 'trust-deed-upload',
  templateUrl: './trust-deed-upload.component.html',
  styleUrls: ['./trust-deed-upload.component.scss'],
  standalone: true,
  imports: [
    FlexModule,
    UploadFileComponent,
    UploadedFilesListComponent,
    MessageBoxComponent,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TrustDeedUploadComponent),
      multi: true,
    },
  ],
})
export class TrustDeedUploadComponent implements OnInit, ControlValueAccessor {
  @Input({ required: true }) applicationId!: number | null;
  /**
   * Derived by the page rather than fetched here, because the page has to know whether any deed is
   * required before it decides to render the step at all.
   */
  @Input({ required: true }) requiredTrustDeeds: RequiredTrustDeed[] = [];
  /** Deeds already in storage, so returning to the step shows what was uploaded earlier. */
  @Input() uploadedDocuments: AzureStorageDocument[] = [];

  @Output() deleteUploadedDoc: EventEmitter<AzureStorageDocument> = new EventEmitter<AzureStorageDocument>();

  /** One per deed box, so the boxes can be emptied once their files are in storage. */
  @ViewChildren(UploadFileComponent) private pickers?: QueryList<UploadFileComponent>;

  subscriptions: Subscription[] = [];

  /** Files chosen in this session, keyed by condition value. */
  private selections = new Map<string, File[]>();

  onChangeFn?: (value: TrustDeedUploadValue) => void;
  onTouchFn?: () => void;
  disabled = false;

  acceptFileTypes = ['.pdf'];

  ngOnInit() {
    setupUntilDestroy(this);
  }

  /** Deeds already uploaded into this condition, matched on the blob's tag. */
  uploadedFor(deed: RequiredTrustDeed): AzureStorageDocument[] {
    const wanted = deed.value.toLowerCase();
    return (this.uploadedDocuments ?? []).filter(doc => {
      const tags = Object.values((doc as any).tags ?? {}).map(tag => String(tag).toLowerCase());
      return tags.includes(wanted);
    });
  }

  onFilesSelected(deed: RequiredTrustDeed, files: File[]) {
    if (files?.length) {
      this.selections.set(deed.value, files);
    } else {
      this.selections.delete(deed.value);
    }
    this.publish();
  }

  onDeleteUploadedDoc(doc: AzureStorageDocument) {
    this.deleteUploadedDoc.emit(doc);
  }

  /**
   * Tagging happens here rather than at upload time so a file can only ever carry the condition of
   * the box it was dropped into.
   */
  private publish() {
    const tagged: UploadAzureFiles = [];
    for (const [conditionValue, files] of this.selections) {
      for (const file of files) {
        (file as UploadAzureFile).tags = [conditionValue];
        tagged.push(file as UploadAzureFile);
      }
    }
    this.onTouchFn?.();
    this.onChangeFn?.(tagged.length ? tagged : null);
  }

  /**
   * The page clears the control once the deeds are uploaded, which has to empty the boxes too: each
   * one keeps its own copy of what was chosen, so clearing only the map above would leave every
   * deed still listed in its box as well as in the uploaded list beneath it.
   */
  writeValue(value: TrustDeedUploadValue): void {
    if (!value || !value.length) {
      this.selections.clear();
      this.pickers?.forEach(picker => {
        picker.filesWithTags = [];
        // Goes through the box's own reset rather than emptying its list directly, so any error it
        // was showing about the files it held is recomputed against the empty box.
        picker.propagateChange(null);
      });
    }
  }

  registerOnChange(fn: (value: TrustDeedUploadValue) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
